/**
 * useOutreachEntryActions — the row-menu/thread-⋯ entry actions that aren't
 * the composer's send (that's useOutreachNegotiation): send-to-brand, close,
 * reopen, pause/resume follow-ups, mark read/unread, remove (single + bulk).
 * Every action but mark read/unread invalidates rows/counts/KPIs campaign-wide
 * plus the one entry's thread. Read/unread write their known outcome into the
 * cache instead: the row's `unread` comes off the Mongo projection, which the
 * fact reaches only after the projection job runs, so an immediate refetch
 * reads the old flag back (G1-a).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OutreachCloseReason, OutreachCounts, OutreachRow } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import { refreshOutreachKpisNow } from './useOutreachKpis';
import {
  clearOutreachBrandCounter,
  clearOutreachEscalation,
  closeOutreach,
  discardOutreachDraft,
  markOutreachRead,
  markOutreachUnread,
  outreachNoReplyNeeded,
  pauseOutreachFollowups,
  sendLapsedFollowupNow,
  skipLapsedFollowup,
  removeOutreachEntries,
  removeOutreachEntry,
  reopenOutreach,
  notUnsubscribeOutreach,
  resumeOutreachFollowups,
  recordOutreachBrandCounter,
  sendOutreachToBrand,
  sendOutreachToBrandMany,
  type ResumeFollowupsResult,
} from '../api/outreachApi';

type RowsPage = { rows: OutreachRow[]; total: number };

/** Both cache shapes under the rows prefix: a table page and the single-row read (null when missing). */
function withUnread(data: unknown, entryId: string, unread: boolean): unknown {
  if (!data || typeof data !== 'object') return data;
  if ('rows' in data && Array.isArray((data as RowsPage).rows)) {
    const page = data as RowsPage;
    return { ...page, rows: page.rows.map((r) => (r.entryId === entryId ? { ...r, unread } : r)) };
  }
  const row = data as OutreachRow;
  return row.entryId === entryId ? { ...row, unread } : data;
}

export function useOutreachEntryActions(campaignId: string) {
  const qc = useQueryClient();
  const invalidate = (entryId?: string) => {
    void qc.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    void qc.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
    void refreshOutreachKpisNow(qc, campaignId);
    if (entryId) void qc.invalidateQueries({ queryKey: campaignKeys.outreachThread(entryId) });
  };
  const invalidateDrafts = () => qc.invalidateQueries({ queryKey: campaignKeys.outreachDraftsAll(campaignId) });
  const applyUnread = (entryId: string, unread: boolean, changed: boolean) => {
    qc.setQueriesData({ queryKey: campaignKeys.outreachRowsAll(campaignId) }, (data: unknown) => withUnread(data, entryId, unread));
    if (changed) {
      qc.setQueryData<OutreachCounts>(campaignKeys.outreachCounts(campaignId), (counts) =>
        counts ? { ...counts, unread: Math.max(0, counts.unread + (unread ? 1 : -1)) } : counts,
      );
    }
    void qc.invalidateQueries({ queryKey: campaignKeys.outreachThread(entryId) });
  };
  // A `use`-prefixed helper: the rules-of-hooks lint recognises it as a hook,
  // and every call below is unconditional, so the call order is fixed per render.
  const useKeyedMutation = <TVars extends { entryId: string }, TOut>(fn: (v: TVars, key: string) => Promise<TOut>) =>
    useMutation<TOut, Error, TVars>({ mutationFn: (v) => fn(v, crypto.randomUUID()), onSuccess: (_o, v) => invalidate(v.entryId) });

  return {
    sendToBrand: useKeyedMutation<{ entryId: string; confirmOpenFlag?: boolean }, void>((v, k) =>
      v.confirmOpenFlag ? sendOutreachToBrand(v.entryId, k, true) : sendOutreachToBrand(v.entryId, k),
    ),
    close: useKeyedMutation<{ entryId: string; reason: OutreachCloseReason }, void>((v, k) => closeOutreach(v.entryId, v.reason, k)),
    discardDraft: useMutation<unknown, Error, { entryId: string; messageId: number }>({
      mutationFn: (v) => discardOutreachDraft(v.entryId, v.messageId, crypto.randomUUID()),
      // A refusal (already approved, discarded or replied) means the row is stale: refetch either way.
      onSettled: (_o, _e, v) => {
        invalidate(v.entryId);
        void invalidateDrafts();
      },
    }),
    reopen: useKeyedMutation<{ entryId: string }, void>((v, k) => reopenOutreach(v.entryId, k)),
    notUnsubscribe: useKeyedMutation<{ entryId: string }, void>((v, k) => notUnsubscribeOutreach(v.entryId, k)),
    pauseFollowups: useKeyedMutation<{ entryId: string }, void>((v, k) => pauseOutreachFollowups(v.entryId, k)),
    resumeFollowups: useKeyedMutation<{ entryId: string }, ResumeFollowupsResult>((v, k) => resumeOutreachFollowups(v.entryId, k)),
    // A 409 NO_LAPSED_FOLLOWUP means the row is stale, so refetch whatever the outcome.
    sendLapsedFollowup: useMutation<void, Error, { entryId: string }>({
      mutationFn: (v) => sendLapsedFollowupNow(v.entryId, crypto.randomUUID()),
      onSettled: (_o, _e, v) => invalidate(v.entryId),
    }),
    skipLapsedFollowup: useMutation<void, Error, { entryId: string }>({
      mutationFn: (v) => skipLapsedFollowup(v.entryId, crypto.randomUUID()),
      onSettled: (_o, _e, v) => invalidate(v.entryId),
    }),
    remove: useKeyedMutation<{ entryId: string; reason?: string }, void>((v, k) => removeOutreachEntry(v.entryId, v.reason ?? '', k)),
    // No onSuccess invalidation: this enqueues a durable RunOperation, and
    // nothing has changed yet — outreach_operation_progress/done (already
    // handled by useOutreachSSE) is what drives the eventual refetch.
    removeMany: useMutation<{ operationId: string }, Error, { entryIds: string[]; reason?: string }>({
      mutationFn: (v) => removeOutreachEntries(campaignId, v.entryIds, v.reason ?? '', crypto.randomUUID()),
    }),
    // No invalidation, like removeMany: the send_to_brand operation's SSE/poll drives the refetch.
    sendToBrandMany: useMutation<{ operationId: string }, Error, { entryIds: string[] }>({
      mutationFn: (v) => sendOutreachToBrandMany(campaignId, v.entryIds, crypto.randomUUID()),
    }),
    recordBrandCounter: useKeyedMutation<{ entryId: string; note?: string }, void>((v, k) => recordOutreachBrandCounter(v.entryId, { note: v.note }, k)),
    clearBrandCounter: useKeyedMutation<{ entryId: string }, void>((v, k) => clearOutreachBrandCounter(v.entryId, k)),
    noReplyNeeded: useKeyedMutation<{ entryId: string; note?: string }, void>((v, k) => outreachNoReplyNeeded(v.entryId, { note: v.note }, k)),
    markRead: useMutation<boolean, Error, { entryId: string }>({ mutationFn: (v) => markOutreachRead(v.entryId), onSuccess: (changed, v) => applyUnread(v.entryId, false, changed) }),
    markUnread: useMutation<boolean, Error, { entryId: string }>({ mutationFn: (v) => markOutreachUnread(v.entryId), onSuccess: (changed, v) => applyUnread(v.entryId, true, changed) }),
    clearEscalation: useKeyedMutation<{ entryId: string; note?: string }, { cleared: boolean }>((v, k) => clearOutreachEscalation(v.entryId, { note: v.note }, k)),
  };
}
